import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["table"];

  connect() {
    this.sortCol = null;
    this.sortDir = "asc";
    this.initializeSortableHeaders();
  }

  initializeSortableHeaders() {
    const table = this.element.querySelector("#query-table");
    if (!table) return;

    const sortableHeaders = table.querySelectorAll("th.sortable");

    sortableHeaders.forEach((header) => {
      header.addEventListener("click", (event) => {
        this.handleSortClick(event, header);
      });
    });
  }

  handleSortClick(event, header) {
    const colIdx = parseInt(header.dataset.colIdx);
    if (this.sortCol === colIdx) {
      this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
    } else {
      this.sortCol = colIdx;
      this.sortDir = "asc";
    }

    this.updateSortIcons(header);
    this.sortTableRows(header);
  }

  updateSortIcons(clickedHeader) {
    const table = this.element.querySelector("#query-table");
    const sortableHeaders = table.querySelectorAll("th.sortable");

    sortableHeaders.forEach((header) => {
      const icon = header.querySelector(".sort-icon");
      if (header === clickedHeader) {
        icon.textContent = this.sortDir === "asc" ? "↑" : "↓";
        icon.classList.remove("opacity-0");
      } else {
        icon.classList.add("opacity-0");
      }
    });
  }

  sortTableRows(header) {
    const table = this.element.querySelector("#query-table");
    const tbody = table.querySelector("tbody");
    const rows = Array.from(tbody.querySelectorAll("tr"));

    const colIdx = this.sortCol;
    const isTrendColumn = header.dataset.trend === "true";

    rows.sort((a, b) => {
      return this.compareRows(a, b, colIdx, isTrendColumn);
    });

    // Clear the tbody and re-append the sorted rows
    tbody.innerHTML = "";
    rows.forEach((row) => tbody.appendChild(row));
  }

  compareRows(a, b, colIdx, isTrendColumn) {
    let valA, valB;

    if (colIdx === 0) {
      // Query column (first column)
      valA = a.querySelector("td").textContent.trim().toLowerCase();
      valB = b.querySelector("td").textContent.trim().toLowerCase();
      return this.sortDir === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    } else if (isTrendColumn) {
      // Trend column (last column)
      const trendOrder = { "↑": 0, "→": 1, "↓": 2, "–": 3 };

      // For trend column, get the last cell
      const cellsA = a.querySelectorAll("td");
      const cellsB = b.querySelectorAll("td");

      const trendCellA = cellsA[cellsA.length - 1];
      const trendCellB = cellsB[cellsB.length - 1];

      if (!trendCellA || !trendCellB) return 0;

      const trendA = trendCellA.textContent.trim();
      const trendB = trendCellB.textContent.trim();

      valA = trendOrder[trendA] ?? 999;
      valB = trendOrder[trendB] ?? 999;
    } else {
      // Date columns (middle columns)
      const cellsA = a.querySelectorAll("td");
      const cellsB = b.querySelectorAll("td");

      // Use colIdx directly as it corresponds to the actual cell position
      const cellA = cellsA[colIdx];
      const cellB = cellsB[colIdx];

      if (!cellA || !cellB) {
        return 0;
      }

      const textA = cellA.textContent.trim();
      const textB = cellB.textContent.trim();

      // Handle special values by assigning them numeric values for comparison
      const getNumericValue = (text) => {
        if (text === "–" || text === "n/a") return Infinity; // Always sort to the end
        const num = parseInt(text);
        return isNaN(num) ? Infinity : num;
      };

      valA = getNumericValue(textA);
      valB = getNumericValue(textB);
    }

    // Handle the case where both values are Infinity (both are "n/a" or "–")
    if (valA === Infinity && valB === Infinity) {
      return 0; // They are equal, so no change in order
    }

    // Handle the case where one value is Infinity (one is "n/a" or "–")
    // "n/a" values should always be at the end, regardless of sort direction
    if (valA === Infinity) {
      return 1; // valA is "n/a", so it should come after valB
    }
    if (valB === Infinity) {
      return -1; // valB is "n/a", so it should come after valA
    }

    // Both values are finite numbers, do normal comparison
    const result = this.sortDir === "asc" ? valA - valB : valB - valA;


    return result;
  }
}
